"use client";

import Image from "next/image";
import Link from "next/link";
import Script from "next/script";
import dynamic from "next/dynamic";
import { useEffect, useState } from "react";
import Navigation from "../../components/Navigation";
import RandomBackground from "../../components/RandomBackground";

const FloatingAnchorMenu = dynamic(
  () => import("@/app/components/FloatingAnchorMenu"),
  { ssr: false },
);

const siteUrl = "https://rainblock.net";

export default function HowToWeatherproofPage() {
  const reg = <sup className="tm-reg">&reg;</sup>;
  const [isHistoryExpanded, setIsHistoryExpanded] = useState(true);
  const floatingMenuItems = [
    { href: "#overview", text: "History" },
    { href: "#safety-warning", text: "Safety Warning" },
    { href: "#what-youll-need", text: "1. What You'll Need" },
    { href: "#choose-right-cord", text: "2. Choose the Right Cord" },
    { href: "#apply-gasket", text: "3. Apply the Gasket" },
    { href: "#seal-open-connections", text: "4. Seal Open Connections" },
    { href: "#connect-multiple-strings", text: "5. Connect Multiple Strings" },
    { href: "#seal-the-end", text: "6. Seal the End" },
    { href: "#position-the-cord", text: "7. Position the Cord" },
    { href: "#shop-now", text: "Shop" },
  ];

  const howToStructuredData = {
    "@context": "https://schema.org",
    "@type": "HowTo",
    name: "How to Protect Outdoor Christmas Light Plugs From Rain",
    description:
      "Step-by-step instructions to protect outdoor Christmas light plugs from rain with waterproof extension cord connector gaskets and covers.",
    image: `${siteUrl}/og-image.jpg`,
    totalTime: "PT30M",
    estimatedCost: {
      "@type": "MonetaryAmount",
      currency: "USD",
      value: "25",
    },
    supply: [
      {
        "@type": "HowToSupply",
        name: "Rain Block® Weatherproof Gaskets",
      },
      {
        "@type": "HowToSupply",
        name: "Seasonal Lighting Weatherproof Kit",
      },
      {
        "@type": "HowToSupply",
        name: "Contractor rated extension cord",
      },
      {
        "@type": "HowToSupply",
        name: "Outdoor rated lights",
      },
      {
        "@type": "HowToSupply",
        name: "Stake to hold extension cord",
      },
    ],
    tool: [
      {
        "@type": "HowToTool",
        name: "Rain Block® products",
      },
    ],
    step: [
      {
        "@type": "HowToStep",
        name: "Safety First",
        text: "Begin by making sure the extension cord or lights are NOT PLUGGED INTO THE OUTLET. Never work with live wires. Always unplug from the outlet before touching or making changes to any outdoor device.",
        image: `${siteUrl}/Itemsneeded.jpg`,
      },
      {
        "@type": "HowToStep",
        name: "Select the Right Extension Cord",
        text: "Start by selecting the right extension cord for the job. It's best to use contractor rated extension cords. Avoid cords with too many openings that will allow water in.",
        image: `${siteUrl}/CorrectCord.jpg`,
      },
      {
        "@type": "HowToStep",
        name: "Apply the Gasket",
        text: "Place a Rain Block® Weatherproof Gasket on the male connector of the light or decoration and firmly press the two together. The gasket will compress slightly and lock out water.",
        image: `${siteUrl}/plug6.jpg`,
      },
      {
        "@type": "HowToStep",
        name: "Seal Open Connections",
        text: "If it is a multi-outlet cord, make sure all open female connections are closed by placing a gasket on a Rain Block® Termination Plug and firmly pressing it into the open connections.",
        image: `${siteUrl}/plug2.jpg`,
      },
      {
        "@type": "HowToStep",
        name: "Connect Multiple Strings",
        text: "You can plug a second string of Christmas lights into the first by applying a gasket to the male plug and firmly press the two together. Daisy chain multiple strands as needed.",
        image: `${siteUrl}/plug4.jpg`,
      },
      {
        "@type": "HowToStep",
        name: "Seal the End",
        text: "Seal off the end of the last light strand by snapping off the third prong of the termination plug, along the score line. Place a gasket on the plug and firmly press it into the female connection.",
        image: `${siteUrl}/plug7.jpg`,
      },
      {
        "@type": "HowToStep",
        name: "Position the Cord",
        text: "Place the cord on a stake above ground and in an upside down position. This position helps keep water dripping off the cord, keeping it from entering the open third ground hole on the cord.",
        image: `${siteUrl}/plugsstake.jpg`,
      },
    ],
    author: {
      "@type": "Organization",
      name: "Rain Block®",
      url: siteUrl,
    },
    publisher: {
      "@type": "Organization",
      name: "Rain Block®",
      logo: {
        "@type": "ImageObject",
        url: `${siteUrl}/LogoWhite.png`,
      },
    },
  };

  useEffect(() => {
    const mobileQuery = globalThis.matchMedia("(max-width: 767px)");
    const syncHistoryState = () => setIsHistoryExpanded(mobileQuery.matches === false);

    syncHistoryState();
    mobileQuery.addEventListener("change", syncHistoryState);
    return () => mobileQuery.removeEventListener("change", syncHistoryState);
  }, []);

  return (
    <div className="relative min-h-screen text-white overflow-hidden">
      <Navigation />

      {/* Fixed background */}
      <RandomBackground />
      {/* Readability overlays */}
      <div
        aria-hidden
        className="fixed inset-0 -z-10 bg-gradient-to-b from-black/75 via-black/25 to-black/10"
      />
      <div
        aria-hidden
        className="fixed inset-0 -z-10 pointer-events-none"
        style={{
          background:
            "radial-gradient(80% 60% at 50% 20%, rgba(0,0,0,0) 0%, rgba(0,0,0,0.15) 100%)",
        }}
      />
      <FloatingAnchorMenu items={floatingMenuItems} scrollShift={-220} />

      {/* Structured Data */}
      <Script
        id="howto-structured-data"
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(howToStructuredData) }}
      />

      {/* Main Content */}
      <div
        className="relative z-10 pt-40 pb-24 px-8 sm:px-6 lg:px-8 xl:pt-24"
      >
        <div className="max-w-6xl mx-auto">
          {/* Page Header */}
          <div id="overview" className="text-center mb-12">
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4">
              How to Protect Outdoor Christmas Light Plugs From Rain
            </h1>
            <p className="text-white/70 text-lg">
              A step-by-step guide to keep outdoor Christmas lights and extension cord connections safe, dry, and running.
            </p>
          </div>

          <div className="mb-10 flex flex-wrap justify-center gap-3">
            <Link
              href="/shop"
              className="rounded-full btn-cta px-5 py-3 font-extrabold text-white transition-all duration-300"
              style={{ textShadow: "none" }}
            >
              Shop Now
            </Link>
            <a
              href="#what-youll-need"
              className="rounded-full border border-white/25 bg-black/35 px-5 py-3 font-bold text-white hover:bg-black/45 transition-all duration-300"
            >
              Learn More
            </a>
          </div>

          <div className="rounded-2xl border border-white/10 bg-black/60 backdrop-blur-glass shadow-glass !bg-black/60 p-6 mb-8">
            <h2 className="text-2xl font-bold text-white mb-3">
              How to keep outdoor Christmas light plugs dry
            </h2>
            <p className="text-white/80">
              If you are asking whether Christmas light plugs can get wet, the safest approach is to seal each exposed
              connection point. Rain Block® gaskets work as a{" "}
              <Link href="/shop" className="text-teal-300 hover:text-teal-200 underline">
                waterproof extension cord connector
              </Link>{" "}
              solution for outdoor plug connections, decorations, and string lights.
            </p>
            <p className="mt-3 text-white/80">
              For full setup, use the steps below and add a{" "}
              <Link href="/shop" className="text-teal-300 hover:text-teal-200 underline">
                weatherproof extension cord cover kit
              </Link>{" "}
              before storms or snowfall.
            </p>
          </div>

          {/* History Section */}
          <div className="rounded-2xl border border-white/10 bg-black/60 backdrop-blur-glass shadow-glass !bg-black/60 mb-8 overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
              <div className="flex items-center gap-3">
                <Image src="/book.svg" alt="" width={34} height={34} aria-hidden />
                <h2 className="text-3xl font-bold text-white">
                  A Brief History of Christmas Lights
                </h2>
              </div>
              {isHistoryExpanded ? (
                <button
                  type="button"
                  onClick={() => setIsHistoryExpanded(false)}
                  className="rounded p-1 hover:bg-white/10 transition-colors"
                  style={{ textShadow: "none" }}
                  aria-label="Collapse history"
                >
                  <Image src="/arrow_up.svg" alt="" width={26} height={26} aria-hidden />
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => setIsHistoryExpanded(true)}
                  className="hidden md:inline-block text-[#FBE6A3] font-semibold hover:text-white transition-colors"
                  style={{ textShadow: "none" }}
                >
                  Read more &gt;
                </button>
              )}
            </div>

            <div className="px-6 py-5 text-white/85">
              <p className="text-xl leading-relaxed">
                <strong>Christmas lights</strong> began as candles on trees in
                18th-century Germany. Electric lights arrived in the 1880s and
                outdoor displays came later.
              </p>
            </div>

            <div className="mx-6 border-t border-white/10" />

            <div className="px-6 py-6">
              <div className="relative pl-8">
                <div className="absolute left-2 top-1 bottom-2 w-px bg-[#fbe6a3]/40" />

                <div className="relative mb-6">
                  <span className="absolute -left-[30px] top-1 h-3 w-3 rounded-full bg-[#FBE6A3] shadow-[0_0_12px_rgba(251,230,163,0.7)]" />
                  <h3 className="text-2xl font-bold text-[#FBE6A3]">
                    18th Century - Germany
                  </h3>
                  <p className="mt-2 text-lg text-white/80">
                    Christmas trees were adopted in homes in 18th-century Germany, where they were occasionally decorated with candles. Because candles were relatively expensive, Christmas trees were primarily found in upper-class homes.
                  </p>
                </div>

                {isHistoryExpanded && (
                  <>
                    <div className="relative mb-6">
                      <span className="absolute -left-[30px] top-1 h-3 w-3 rounded-full bg-[#FBE6A3] shadow-[0_0_12px_rgba(251,230,163,0.7)]" />
                      <h3 className="text-2xl font-bold text-[#FBE6A3]">
                        1880s - The Birth of Electric Christmas Lights
                      </h3>
                      <p className="mt-2 text-lg text-white/80">
                        Early electric Christmas lights were introduced in the 1880s with the arrival of electricity.
                      </p>
                    </div>

                    <div className="relative mb-6">
                      <span className="absolute -left-[30px] top-1 h-3 w-3 rounded-full bg-[#FBE6A3] shadow-[0_0_12px_rgba(251,230,163,0.7)]" />
                      <h3 className="text-2xl font-bold text-[#FBE6A3]">
                        1882 - Edward Johnson&apos;s Electric Christmas Tree
                      </h3>
                      <p className="mt-2 text-lg text-white/80">
                        The first known Christmas tree lit by electric light was created by Edward Johnson, an associate of Thomas Edison and vice president of the Edison Electric Light Company.
                      </p>
                      <p className="mt-2 text-lg text-white/80">
                        Johnson&apos;s tree was wired with 80 red, white, and blue, walnut-sized incandescent bulbs and displayed on December 22, 1882.
                      </p>
                      <p className="mt-2 text-lg text-white/80">
                        After being featured in a Detroit newspaper, Johnson became known as the Father of Electric Christmas Tree Lights. Because electric lights were still expensive, they did not appear in most households until 1930.
                      </p>
                    </div>

                    <div className="relative mb-6">
                      <span className="absolute -left-[30px] top-1 h-3 w-3 rounded-full bg-[#FBE6A3] shadow-[0_0_12px_rgba(251,230,163,0.7)]" />
                      <h3 className="text-2xl font-bold text-[#FBE6A3]">
                        1904-1912 - Christmas Lights Go Outdoors
                      </h3>
                      <p className="mt-2 text-lg text-white/80">Christmas lights were used exclusively indoors until:</p>
                      <ul className="mt-2 list-disc pl-6 text-lg text-white/80 space-y-1">
                        <li>1904 in San Diego</li>
                        <li>1909 in Appleton, Wisconsin</li>
                        <li>1912 in New York City</li>
                      </ul>
                      <p className="mt-2 text-lg text-white/80">
                        These mark the first recorded instances of Christmas lights being displayed outdoors.
                      </p>
                    </div>

                    <div className="relative mb-3">
                      <span className="absolute -left-[30px] top-1 h-3 w-3 rounded-full bg-[#FBE6A3] shadow-[0_0_12px_rgba(251,230,163,0.7)]" />
                      <h3 className="text-2xl font-bold text-[#FBE6A3]">
                        1956 - Outdoor Displays Become Tradition
                      </h3>
                      <p className="mt-2 text-lg text-white/80">
                        McAdenville, North Carolina claims to have been the first town to create a large-scale outdoor Christmas light display in 1956.
                      </p>
                      <p className="mt-2 text-lg text-white/80">
                        Once lights moved outdoors, rain became a problem and it still is today.
                      </p>
                    </div>

                    <a
                      href="#apply-gasket"
                      className="inline-block mt-4 text-[#FBE6A3] text-2xl font-semibold hover:text-white transition-colors"
                      style={{ textShadow: "none" }}
                    >
                      Skip to Step 1 -&gt;
                    </a>
                  </>
                )}
              </div>
            </div>

            {isHistoryExpanded && (
              <button
                type="button"
                onClick={() => setIsHistoryExpanded(false)}
                className="w-full border-t border-white/10 py-3 flex items-center justify-center gap-2 text-white/85 hover:bg-white/5 transition-colors"
                style={{ textShadow: "none" }}
              >
                <Image
                  src="/expand_circle_up.svg"
                  alt=""
                  width={20}
                  height={20}
                  aria-hidden
                  className="transition-transform duration-300"
                />
                <span>Collapse</span>
              </button>
            )}

            {!isHistoryExpanded && (
              <button
                type="button"
                onClick={() => setIsHistoryExpanded(true)}
                className="md:hidden w-full border-t border-white/10 py-3 flex items-center justify-center gap-2 text-[#FBE6A3] font-semibold hover:bg-white/5 hover:text-white transition-colors"
                style={{ textShadow: "none" }}
              >
                <span>Read more &gt;</span>
              </button>
            )}
          </div>

          {/* Warning Box */}
          <div
            id="safety-warning"
            className="rounded-2xl border border-white/10 bg-black/60 backdrop-blur-glass shadow-glass !bg-black/60 p-8 mb-8"
          >
            <div className="flex items-center gap-5 mb-6">
              <div
                className="h-24 w-24 flex items-center justify-center"
                style={{ textShadow: "none" }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 96 96"
                  width="96"
                  height="96"
                  aria-hidden
                >
                  <circle
                    cx="48"
                    cy="48"
                    r="44"
                    fill="rgba(239,68,68,0.15)"
                    stroke="rgba(248,113,113,0.35)"
                    strokeWidth="3"
                  />
                  <path
                    d="M48 18 L78 72 H18 Z"
                    fill="rgba(251,191,36,0.95)"
                    stroke="rgba(251,191,36,0.35)"
                    strokeWidth="2"
                  />
                  <rect x="45.5" y="34" width="5" height="24" rx="2.5" fill="#111827" />
                  <circle cx="48" cy="66" r="3.2" fill="#111827" />
                </svg>
              </div>
              <div>
                <h3 className="text-xl md:text-[26px] leading-tight font-extrabold text-red-400 tracking-wide">
                  WARNING
                </h3>
                <p className="text-base md:text-lg leading-tight text-white/80">Safety first</p>
              </div>
            </div>
            <ul className="space-y-4 text-base md:text-lg text-white/90 leading-relaxed">
              <li className="flex gap-3">
                <span className="text-base md:text-lg">&bull;</span>
                <span>
                  Begin by making sure the extension cord or lights are <strong>NOT PLUGGED</strong> into the outlet.
                </span>
              </li>
              <li className="flex gap-3">
                <span className="text-base md:text-lg">&bull;</span>
                <span>
                  Never work with live wires. Always <strong>unplug</strong> from the outlet before touching changes.
                </span>
              </li>
              <li className="flex gap-3">
                <span className="text-base md:text-lg">&bull;</span>
                <span>
                  Use <strong>Rain Block{reg} gaskets/kits</strong> only with a properly tested, functioning GFCI <strong>outlet</strong>.
                </span>
              </li>
            </ul>
          </div>

          {/* Items Needed Section */}
          <div
            id="what-youll-need"
            className="rounded-2xl border border-white/10 bg-black/60 backdrop-blur-glass shadow-glass !bg-black/60 mb-10 overflow-hidden"
          >
            <div className="p-8">
              <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.25fr] gap-4 md:gap-5 items-start">
                <div className="text-center">
                  <Image
                    src="/Itemsneeded.jpg"
                    alt="Items needed to weatherproof Christmas lights"
                    width={327}
                    height={193}
                    className="mx-auto rounded-lg border border-white/15"
                  />
                  <p className="text-white/75 text-sm md:text-base leading-relaxed mt-3">
                    (Fig. 1) Items needed to weatherproof Christmas lights.
                  </p>
                </div>
                <div>
                  <h3 className="text-xl md:text-[26px] leading-tight font-extrabold text-white mb-6">
                    Items Needed
                  </h3>
                  <ul className="space-y-4 text-sm md:text-base text-white/85 leading-relaxed">
                    <li className="flex items-start gap-3">
                      <Image src="/check_small.svg" alt="" width={22} height={22} aria-hidden className="mt-1" />
                      <span className="md:font-semibold">
                        At least one pack of{' '}
                        <Link href="/shop" className="text-teal-300 hover:text-teal-200 underline">
                          Rain Block{reg} Weatherproof Gaskets
                        </Link>
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Image src="/check_small.svg" alt="" width={22} height={22} aria-hidden className="mt-1" />
                      <span className="md:font-semibold">
                        At least one pack of{' '}
                        <Link href="/shop" className="text-teal-300 hover:text-teal-200 underline">
                          Seasonal Lighting Weatherproof Kit
                        </Link>
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Image src="/check_small.svg" alt="" width={22} height={22} aria-hidden className="mt-1" />
                      <span>A contractor-rated extension cord (preferably rated for 15 Amps or above)</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Image src="/check_small.svg" alt="" width={22} height={22} aria-hidden className="mt-1" />
                      <span>Outdoor-rated lights</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Image src="/check_small.svg" alt="" width={22} height={22} aria-hidden className="mt-1" />
                      <span>Stake to hold extension cord in an inverted position</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="border-t border-white/15 px-8 py-4 flex items-center gap-3 text-sm md:text-base leading-relaxed text-white/85">
              <Image src="/post_add.svg" alt="" width={28} height={28} aria-hidden />
              <p>
                <strong>Recommended:</strong> Choose cords with no openings in the connector body.
              </p>
            </div>
          </div>

          {/* Step-by-Step Instructions */}
          <div className="space-y-8">
            <div
              id="choose-right-cord"
              className="rounded-2xl border border-white/10 bg-black/60 backdrop-blur-glass shadow-glass !bg-black/60 p-8"
            >
              <div className="grid grid-cols-1 lg:grid-cols-[auto_1fr] gap-3 md:gap-4 items-start">
                <div className="text-center">
                  <div className="p-3 space-y-4">
                    <div className="relative inline-block">
                      <Image
                        src="/wontwork.jpg"
                        alt="Extension cord that will not work"
                        width={160}
                        height={120}
                        style={{ height: "auto" }}
                        className="mx-auto mb-2 rounded-lg"
                      />
                      <div
                        aria-hidden
                        className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-lg text-6xl font-extrabold text-red-500/85"
                        style={{ textShadow: "none" }}
                      >
                        X
                      </div>
                      <p className="text-white/75 text-sm">
                        (fig. 2) The type extension cord pictured will NOT work.
                      </p>
                    </div>
                    <div>
                    <Image
                      src="/CorrectCord.jpg"
                      alt="Correct multi-plug cord"
                      width={160}
                      height={120}
                      style={{ height: "auto" }}
                      className="mx-auto mb-2 rounded-lg"
                    />
                    <p className="text-white/75 text-sm">(fig. 3) Multi-plug cord.</p>
                    </div>
                  </div>
                </div>
                <div>
                  <h4 className="text-xl font-semibold text-white mb-4">
                    Selecting the Right Extension Cord
                  </h4>
                  <p className="text-white/75">
                    Start by selecting the right extension cord for the job.
                    It&apos;s best to use contractor rated extension cords such
                    as the one pictured in <em>fig 1</em>. The style pictured
                    in <em>fig 2</em> will NOT work. There are too many
                    openings that will allow water in. Thanks to the Rain Block®
                    Seasonal Weatherproof Kit, you can also weatherproof
                    multi-plug cords, similar to the one shown in <em>fig 3</em>.
                    The cord in the photo has an LED that we sealed with
                    regular clear silicone. If your cord has any openings like
                    that it is best to seal it with silicone. It&apos;s best to
                    choose cords with no openings.
                  </p>
                </div>
              </div>
            </div>

            <div
              id="apply-gasket"
              className="rounded-2xl border border-white/10 bg-black/60 backdrop-blur-glass shadow-glass !bg-black/60 p-8"
            >
              <div className="grid grid-cols-1 lg:grid-cols-[auto_1fr] gap-3 md:gap-4 items-start">
                <div className="text-center">
                  <div className="p-3">
                    <Image
                      src="/plug6.jpg"
                      alt="Multi-prong cord"
                      width={160}
                      height={120}
                      style={{ height: "auto" }}
                      className="mx-auto mb-4 rounded-lg"
                    />
                    <p className="text-white/75 text-sm">(fig. 4) Multi-prong cord.</p>
                  </div>
                </div>
                <div>
                  <h4 className="text-xl font-semibold text-white mb-4">
                    Apply the Gasket
                  </h4>
                  <p className="text-white/75">
                    Once we have verified that everything is unplugged, we begin
                    at the extension cord. Place a Rain Block{reg} Weatherproof
                    Gasket on the male connector of the light or decoration and
                    firmly press the two together. The gasket will compress
                    slightly and lock out water.
                  </p>
                </div>
              </div>
            </div>

            <div
              id="seal-open-connections"
              className="rounded-2xl border border-white/10 bg-black/60 backdrop-blur-glass shadow-glass !bg-black/60 p-8"
            >
              <div className="grid grid-cols-1 lg:grid-cols-[auto_1fr] gap-3 md:gap-4 items-start">
                <div className="text-center">
                  <div className="p-3">
                    <Image
                      src="/plug2.jpg"
                      alt="Seal open female connections"
                      width={160}
                      height={120}
                      style={{ height: "auto" }}
                      className="mx-auto mb-4 rounded-lg"
                    />
                    <p className="text-white/75 text-sm">
                      (fig. 5) Seal all open female connections.
                    </p>
                  </div>
                </div>
                <div>
                  <h4 className="text-xl font-semibold text-white mb-4">
                    Seal Open Connections
                  </h4>
                  <p className="text-white/75">
                    If it is a multi-outlet cord, like the one shown, make sure
                    all open female connections are closed by placing a gasket
                    on a Rain Block{reg} Termination Plug and firmly pressing it
                    into the open connections. Also seal off the back of the
                    light plug&apos;s female connection the same way, unless you
                    plan to plug another string of lights into the open
                    connection.
                  </p>
                </div>
              </div>
            </div>

            <div
              id="connect-multiple-strings"
              className="rounded-2xl border border-white/10 bg-black/60 backdrop-blur-glass shadow-glass !bg-black/60 p-8"
            >
              <div className="grid grid-cols-1 lg:grid-cols-[auto_1fr] gap-3 md:gap-4 items-start">
                <div className="text-center">
                  <div className="p-3">
                    <Image
                      src="/plug4.jpg"
                      alt="Connecting multiple light strands"
                      width={160}
                      height={120}
                      style={{ height: "auto" }}
                      className="mx-auto mb-4 rounded-lg"
                    />
                    <p className="text-white/75 text-sm">
                      (fig. 6) Connecting multiple light strands.
                    </p>
                  </div>
                </div>
                <div>
                  <h4 className="text-xl font-semibold text-white mb-4">
                    Connect Multiple Strings
                  </h4>
                  <p className="text-white/75">
                    You can plug a second string of Christmas lights into the
                    first by applying a gasket to the male plug and firmly press
                    the two together. From there you can daisy chain multiple
                    Christmas light strands together by applying a gasket to the
                    male connection and firmly pressing it into the end female
                    connector. Don&apos;t forget to close off the back with an end
                    plug and gasket.
                  </p>
                </div>
              </div>
            </div>

            <div
              id="seal-the-end"
              className="rounded-2xl border border-white/10 bg-black/60 backdrop-blur-glass shadow-glass !bg-black/60 p-8"
            >
              <div className="grid grid-cols-1 lg:grid-cols-[auto_1fr] gap-3 md:gap-4 items-start">
                <div className="text-center">
                  <div className="p-3">
                    <Image
                      src="/plug7.jpg"
                      alt="Close off the end connection"
                      width={160}
                      height={120}
                      style={{ height: "auto" }}
                      className="mx-auto mb-4 rounded-lg"
                    />
                    <p className="text-white/75 text-sm">
                      (fig. 7) Close off the end connection.
                    </p>
                  </div>
                </div>
                <div>
                  <h4 className="text-xl font-semibold text-white mb-4">
                    Seal the End
                  </h4>
                  <p className="text-white/75">
                    Seal off the end of the last light strand by snapping off
                    the third prong of the termination plug, along the score
                    line. Place a gasket on the plug and firmly press it into
                    the female connection.
                  </p>
                </div>
              </div>
            </div>

            <div
              id="position-the-cord"
              className="rounded-2xl border border-white/10 bg-black/60 backdrop-blur-glass shadow-glass !bg-black/60 p-8"
            >
              <div className="grid grid-cols-1 lg:grid-cols-[auto_1fr] gap-3 md:gap-4 items-start">
                <div className="text-center">
                  <div className="p-3">
                    <Image
                      src="/plugsstake.jpg"
                      alt="Hang the extension cord upside down"
                      width={160}
                      height={120}
                      style={{ height: "auto" }}
                      className="mx-auto mb-4 rounded-lg"
                    />
                    <p className="text-white/75 text-sm">
                      (fig. 8) Hang the extension cord upside down.
                    </p>
                  </div>
                </div>
                <div>
                  <h4 className="text-xl font-semibold text-white mb-4">
                    Position the Cord
                  </h4>
                  <p className="text-white/75">
                    It is best if the cord is then placed on a stake above
                    ground and in an upside down position, as shown in{" "}
                    <em>fig 8</em>. This position helps keep water dripping off
                    the cord, keeping it from entering the open third ground
                    hole on the cord.
                  </p>
                  <p className="text-teal-300 font-semibold text-lg mt-4">
                    That&apos;s all there is to it. Weatherproofing Christmas
                    lights is now simple and reliable.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div id="shop-now" className="text-center mt-12">
            <h3 className="text-2xl font-bold text-white mb-4">
              Ready to Weatherproof Your Christmas Lights?
            </h3>
            <p className="text-white/70 text-lg mb-8">
              Get the Rain Block{reg} products you need to protect your outdoor
              electrical connections.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                href="/shop"
                className="rounded-full btn-cta px-5 py-3 font-extrabold text-white transition-all duration-300"
                style={{ textShadow: "none" }}
              >
                Shop Now
              </Link>
              <Link
                href="/faq"
                className="rounded-full border border-white/30 bg-white/10 px-8 py-3 font-semibold text-white hover:bg-white/20 transition-all duration-300"
              >
                View FAQ
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}







